import React, { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getDashboardStats } from '../../services/DashboardService';

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 24px 0;
  font-size: ${({ theme }) => theme.typography.h2.size};
  font-weight: ${({ theme }) => theme.typography.h2.weight};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px 24px;
  box-shadow: ${({ theme }) => theme.shadows.md};
  display: flex;
  flex-direction: column;
  gap: 6px;
  border-left: 4px solid ${({ $color }) => $color};
`;

const StatIcon = styled.div`
  font-size: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  line-height: 1;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.lineColor};
`;

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 16px;
`;

const ChartCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: 20px 24px;
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const ChartTitle = styled.h3`
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 8px 0;
  font-size: 1rem;
  font-weight: 600;
`;

const BOOK_COLORS = ['#4caf50', '#1890ff', '#ff9800'];
const USER_COLORS = ['#4caf50', '#1890ff', '#9c27b0', '#8c8c8c'];

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();

  useEffect(() => {
    getDashboardStats()
      .then(setStats)
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <PageWrapper><p style={{ color: theme.colors.text }}>Učitavanje...</p></PageWrapper>;
  }
  if (error) {
    return <PageWrapper><p style={{ color: theme.colors.error }}>{error}</p></PageWrapper>;
  }

  const bookCopiesData = [
    { name: 'Dostupno', value: Number(stats.availableCopies) },
    { name: 'Posuđeno', value: Number(stats.borrowedCopies) },
    { name: 'Rezervirano', value: Number(stats.reservedCopies) },
  ].filter(d => d.value > 0);

  const usersData = [
    { name: 'Aktivni', value: Number(stats.activeUsers) },
    { name: 'Studenti', value: Number(stats.studentUsers) },
    { name: 'Umirovljeni', value: Number(stats.retiredUsers) },
    { name: 'Neaktivni', value: Number(stats.inactiveUsers) },
  ].filter(d => d.value > 0);

  const tooltipStyle = {
    backgroundColor: theme.colors.card,
    border: `1px solid ${theme.colors.lineColor}`,
    color: theme.colors.text,
    borderRadius: '8px',
  };

  return (
    <PageWrapper>
      <Title>Dashboard</Title>

      <CardsGrid>
        <StatCard $color="#1890ff">
          <StatValue>{stats.totalBooks}</StatValue>
          <StatLabel>Ukupno knjiga</StatLabel>
        </StatCard>
        <StatCard $color="#4caf50">
          <StatValue>{stats.activeBorrowings}</StatValue>
          <StatLabel>Aktivne posudbe</StatLabel>
        </StatCard>
        <StatCard $color="#f44336">
          <StatValue>{stats.overdueBorrowings}</StatValue>
          <StatLabel>Prekoračeni rok</StatLabel>
        </StatCard>
        <StatCard $color="#ff9800">
          <StatValue>{stats.activeReservations}</StatValue>
          <StatLabel>Aktivne rezervacije</StatLabel>
        </StatCard>
      </CardsGrid>

      <ChartsGrid>
        <ChartCard>
          <ChartTitle>Dostupnost primjeraka</ChartTitle>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={bookCopiesData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
              >
                {bookCopiesData.map((entry, index) => (
                  <Cell key={`book-cell-${index}`} fill={BOOK_COLORS[index % BOOK_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', color: theme.colors.lineColor, fontSize: '0.8rem', marginTop: '4px' }}>
            Ukupno primjeraka: {stats.totalCopies}
          </div>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Korisnici po statusu</ChartTitle>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={usersData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={3}
                dataKey="value"
              >
                {usersData.map((entry, index) => (
                  <Cell key={`user-cell-${index}`} fill={USER_COLORS[index % USER_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </ChartsGrid>
    </PageWrapper>
  );
}

export default Dashboard;
